import { useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { Button } from '@components/Button';
import { PlusIcon } from '@icons/PlusIcon';
import { IntegrationType } from '@insights_module/types/Integrations';
import { INTEGRATION_TITLE_MAPPER } from '@insights_module/constants/integrations';
import { CloseIcon } from '@icons/CloseIcon';
import { EyeIcon, EyeIconCrossed } from '@icons/EyeIcon';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { SidebarToggleButton } from '@insights_module/components/SidebarToggleButton';
import { SearchBar } from '@insights_module/components/SearchBar';
import { GridIcon } from '@icons/GridIcon';
import { ListIcon } from '@icons/ListIcon';
import { useUser } from '@auth_module/hooks/useUser';
import { useOrganizationUsers } from '@account_module/hooks/useOrganizationUsers';
import { USER_ROLE_KEY } from '@account_module/constants/userKeys';

const Container = tw.div`w-full h-[41px] flex items-center justify-between px-[24px] py-1.5 border-b border-solid border-gray-1`;
const SecondaryHeader = tw.div`flex items-center justify-end px-[24px] pt-[8px]`;
const Title = tw.span`text-[15px] text-text-and-icon font-semibold capitalize`;
const Flex = tw.div`flex items-center gap-2`;
const Separator = tw.div`w-[1px] h-5 bg-gray-1`;
const ActionButton = tw(
  Button.Primary
)`text-[11px] p-[6px] w-auto h-[26px] inline-flex items-center justify-between gap-[6px] bg-accent-2 border border-accent-2 disabled:cursor-not-allowed`;
const DeleteBtn = tw(ActionButton)`text-error-text-icon bg-error-bg border-error-bg  px-3`;
const LayoutButton = tw.button`p-[6px] h-[26px]`;

export const Header = ({ type }: { type: IntegrationType }) => {
  const { isEnabled: isMegaphoneTrackingPixelEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_TRACKING_PIXEL
  );
  const { organization } = useUser();
  const { currentUser } = useOrganizationUsers(organization?.id);

  const isAdmin = currentUser.role === USER_ROLE_KEY.admin;
  const isViewer = currentUser.role === USER_ROLE_KEY.viewer;

  const { t } = useTranslation();
  const {
    selectedRows,
    showCreateTrackingPixelModal,
    showTrackingPixelTableModal,
    layoutType,
    searchTerm,
    setIsCreateTagModalOpen,
    setIsDeleteTagModalOpen,
    setShowCreateTrackingPixelModal,
    setShowTrackingPixelTableModal,
    setLayoutType,
    setSearchTerm,
  } = useIntegrationsContext();

  const copy: {
    count: string;
    button: string;
  } = useMemo(() => {
    const isBulk = selectedRows.length > 1;

    return {
      count: isBulk
        ? t('insights.integrations.megaphone.header.selected_tag.plural', {
            count: selectedRows.length,
          })
        : t('insights.integrations.megaphone.header.selected_tag.singular'),
      button: isBulk
        ? t('insights.integrations.megaphone.header.delete_button.plural')
        : t('insights.integrations.megaphone.header.delete_button.singular'),
    };
  }, [selectedRows]);

  return (
    <>
      <Container>
        <div tw='flex items-center gap-[16px]'>
          <SidebarToggleButton />
          <Title>{INTEGRATION_TITLE_MAPPER[type]} Integration</Title>
          <SearchBar
            value={searchTerm}
            onChange={(val) => setSearchTerm(val)}
            css={{ borderRadius: '4px' }}
            placeholder={t('common.search')}
            inputStyles={tw`py-[6px] text-[12px] placeholder:text-[12px]`}
          />
        </div>

        <Flex>
          <ActionButton
            onClick={() => setIsCreateTagModalOpen(true)}
            data-testid='create-new-collection-cta'
            disabled={isViewer}>
            <PlusIcon tw='stroke-text-and-icon-invert' />
            {t('insights.integrations.megaphone.no_tags_fallback.cta')}
          </ActionButton>
          {isMegaphoneTrackingPixelEnabled && (
            <>
              <Separator />
              <ActionButton
                css={[!showCreateTrackingPixelModal && tw`bg-sec-4 border-sec-3 text-accent-1`]}
                onClick={() => setShowCreateTrackingPixelModal(!showCreateTrackingPixelModal)}>
                Create Tracking Pixel
                {showCreateTrackingPixelModal ? (
                  <CloseIcon tw='fill-text-and-icon-invert w-[12px] h-[8px]' />
                ) : (
                  <PlusIcon tw='stroke-accent-1' />
                )}
              </ActionButton>
              <ActionButton
                css={[
                  !showTrackingPixelTableModal && tw`bg-sec-4 border border-sec-3 text-accent-1`,
                ]}
                onClick={() => setShowTrackingPixelTableModal(!showTrackingPixelTableModal)}>
                Pixel List
                {showTrackingPixelTableModal ? (
                  <EyeIconCrossed tw='stroke-text-and-icon-invert fill-none w-[12px] h-[12px]' />
                ) : (
                  <EyeIcon tw='stroke-accent-1 fill-none w-[12px]' />
                )}
              </ActionButton>
            </>
          )}
        </Flex>
      </Container>
      <SecondaryHeader>
        {selectedRows.length > 0 && (
          <Flex>
            <p tw='text-text-and-icon-muted'>{copy?.count}</p>
            <DeleteBtn
              disabled={!isAdmin}
              onClick={() => (isAdmin ? setIsDeleteTagModalOpen(true) : null)}>
              {copy?.button}
            </DeleteBtn>
            <Separator />
          </Flex>
        )}
        <LayoutButton
          data-testid='integration-layout-toggler-grid'
          css={[layoutType === 'grid' && tw`border-b border-solid border-text-and-icon`]}
          onClick={() => setLayoutType('grid')}>
          <GridIcon
            css={[
              tw`w-[12px] h-[12px]`,
              layoutType === 'grid' ? tw`stroke-text-and-icon` : tw`stroke-text-and-icon-muted-2`,
            ]}
          />
        </LayoutButton>
        <LayoutButton
          data-testid='integration-layout-toggler-table'
          css={[layoutType === 'table' && tw`border-b border-solid border-text-and-icon`]}
          onClick={() => setLayoutType('table')}>
          <ListIcon
            css={[
              tw`w-[12px] h-[12px]`,
              layoutType === 'table' ? tw`stroke-text-and-icon` : tw`stroke-text-and-icon-muted-2`,
            ]}
          />
        </LayoutButton>
      </SecondaryHeader>
    </>
  );
};
